import { createSlice } from '@reduxjs/toolkit';
import axiosInstance from 'src/utils/axiosInstance';

const initialState = {
  permissions: [],
  totalCount: 0,
  totalPages: 0,
  loading: false,
  error: null,
};

export const permissionSlice = createSlice({
  name: 'permission',
  initialState,
  reducers: {
    setPermissions: (state, action) => {
      state.permissions = action.payload;
    },
    setTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    setTotalPages: (state, action) => {
      state.totalPages = action.payload;
    },
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
  },
});

export const { setPermissions, setTotalCount, setTotalPages, setLoading, setError } = permissionSlice.actions;

// Get permissions (paginated)
export const getPermissions = (filters) => async (dispatch) => {
  dispatch(setLoading(true));
  dispatch(setError(null));
  try {
    const page = filters.page || 1;
    const limit = filters.limit || 10;
    const search = filters.search || '';
    const response = await axiosInstance.get('/admin/permission/list', {
      params: { page, limit, search }
    });
    if (response.data && response.data.status === true) {
      const result = response.data.data.result || [];
      const totalCount = response.data.data.totalCount || result.length;
      const totalPages = response.data.data.totalPages || 1;
      dispatch(setPermissions(result));
      dispatch(setTotalCount(totalCount));
      dispatch(setTotalPages(totalPages));
      return { status: true, data: result, totalCount, totalPages };
    } else {
      const errorMsg = response.data?.message || 'Failed to fetch permissions';
      dispatch(setError(errorMsg));
      return { status: false, message: errorMsg };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch permissions';
    dispatch(setError(errorMsg));
    return { status: false, message: errorMsg };
  } finally {
    dispatch(setLoading(false));
  }
};

// Create a permission
export const createPermission = (payload) => async () => {
  try {
    const response = await axiosInstance.post('/admin/permission/add', payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to create permission' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to create permission';
    return { status: false, message: errorMsg };
  }
};

// Update a permission
export const updatePermission = (id, payload) => async () => {
  try {
    const response = await axiosInstance.put(`/admin/permission/update/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to update permission' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to update permission';
    return { status: false, message: errorMsg };
  }
};

// Get permission details
export const getPermissionDetails = (id) => async () => {
  try {
    const response = await axiosInstance.get(`/admin/permission/getbyId/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to fetch permission details' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to fetch permission details';
    return { status: false, message: errorMsg };
  }
};

// Toggle permission status
export const togglePermissionStatus = (id, payload) => async () => {
  try {
    const response = await axiosInstance.patch(`/admin/permission/status/${id}`, payload);
    if (response.data && response.data.status === true) {
      return { status: true, data: response.data.data, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to toggle permission status' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to toggle permission status';
    return { status: false, message: errorMsg };
  }
};

// Delete a permission
export const deletePermission = (id) => async () => {
  try {
    const response = await axiosInstance.delete(`/admin/permission/delete/${id}`);
    if (response.data && response.data.status === true) {
      return { status: true, message: response.data?.message };
    } else {
      return { status: false, message: response.data?.message || 'Failed to delete permission' };
    }
  } catch (error) {
    const errorMsg = error.response?.data?.message || error.message || 'Failed to delete permission';
    return { status: false, message: errorMsg };
  }
};

export default permissionSlice.reducer;
